/**
 * Enrollment Service
 *
 * Handles CRUD and status transitions for ActivityEnrollments.
 */

import { apiClient } from '@/shared/services/api';
import {
  ActivityEnrollment,
  EnrollmentFormData,
  EnrollmentFilters,
  EnrollmentStatus,
} from '../types';

// ── Status mapping ─────────────────────────────────────────────────────────────
// Backend ActivityEnrollment: 'enrolled' | 'attended' | 'no_show' | 'cancelled'
// Frontend EnrollmentStatus matches backend exactly — no conversion needed.
// Backend fields (snake_case) are transformed to camelCase by apiClient.

function mapEnrollment(raw: ActivityEnrollment): ActivityEnrollment {
  // Explicit mapping of all backend ActivityEnrollment fields:
  // Required: id, activity, status, priceCharged, enrolledVia, enrolledAt
  // Guest participant: guest, guestName, booking
  // Staff participant: staff, staffName
  // External participant: externalParticipantName, externalParticipantNotes
  // Charge tracking: guestCharge, hasCharge, hasStaffExpense
  // Settlement info: settlementMode, settlementSourceEntityType, settlementSourceEntityId
  // Display: displayName, participantType
  // Lifecycle: cancelledAt
  //
  // All fields are already camelCased by apiClient, no further transformation needed.
  return {
    id: raw.id,
    activity: raw.activity,
    guest: raw.guest,
    guestName: raw.guestName,
    booking: raw.booking,
    staff: raw.staff,
    staffName: raw.staffName,
    externalParticipantName: raw.externalParticipantName,
    externalParticipantNotes: raw.externalParticipantNotes,
    status: raw.status,
    priceCharged: raw.priceCharged,
    enrolledVia: raw.enrolledVia,
    enrolledAt: raw.enrolledAt,
    cancelledAt: raw.cancelledAt,
    guestCharge: raw.guestCharge,
    hasCharge: raw.hasCharge,
    hasStaffExpense: raw.hasStaffExpense,
    settlementMode: raw.settlementMode,
    settlementSourceEntityType: raw.settlementSourceEntityType,
    settlementSourceEntityId: raw.settlementSourceEntityId,
    displayName: raw.displayName,
    participantType: raw.participantType,
  };
}

// ── Enrollments ────────────────────────────────────────────────────────────────

export async function fetchEnrollments(filters?: EnrollmentFilters): Promise<ActivityEnrollment[]> {
  const params: Record<string, string> = {};
  if (filters?.activityId) params.activity = filters.activityId;
  if (filters?.status) params.status = filters.status;
  if (filters?.guestId) params.guest = filters.guestId;
  if (filters?.staffId) params.staff = filters.staffId;
  if (filters?.activityDateFrom) params.activity_date_from = filters.activityDateFrom;
  if (filters?.activityDateTo) params.activity_date_to = filters.activityDateTo;

  const response = await apiClient.get<ActivityEnrollment[] | { results: ActivityEnrollment[] }>(
    '/api/v1/activity-enrollments/',
    { params }
  );
  const data = response.data;
  const list = Array.isArray(data) ? data : data.results;
  return list.map(mapEnrollment);
}

export async function fetchEnrollment(id: string): Promise<ActivityEnrollment> {
  const response = await apiClient.get<ActivityEnrollment>(`/api/v1/activity-enrollments/${id}/`);
  return mapEnrollment(response.data);
}

export async function createEnrollment(
  activityId: string,
  data: EnrollmentFormData
): Promise<ActivityEnrollment> {
  const payload: Record<string, unknown> = {
    activity: activityId,
  };
  if (data.guestId !== undefined) payload.guest = data.guestId;
  if (data.bookingId !== undefined) payload.booking = data.bookingId;
  if (data.staffId !== undefined) payload.staff = data.staffId;
  if (data.externalParticipantName !== undefined) payload.external_participant_name = data.externalParticipantName;
  if (data.externalParticipantNotes !== undefined) payload.external_participant_notes = data.externalParticipantNotes;
  if (data.enrolledVia !== undefined) payload.enrolled_via = data.enrolledVia;
  if (data.waitlistIfFull !== undefined) payload.waitlist_if_full = data.waitlistIfFull;

  const response = await apiClient.post<ActivityEnrollment>(
    `/api/v1/activities/${activityId}/enrollments/`,
    payload
  );
  return mapEnrollment(response.data);
}

export async function attendEnrollment(id: string): Promise<ActivityEnrollment> {
  const response = await apiClient.post<ActivityEnrollment>(
    `/api/v1/activity-enrollments/${id}/attend/`
  );
  return mapEnrollment(response.data);
}

export async function noShowEnrollment(id: string): Promise<ActivityEnrollment> {
  const response = await apiClient.post<ActivityEnrollment>(
    `/api/v1/activity-enrollments/${id}/no-show/`
  );
  return mapEnrollment(response.data);
}

export async function cancelEnrollment(
  id: string,
  options?: { voidCharge?: boolean; voidStaffExpense?: boolean }
): Promise<ActivityEnrollment> {
  const payload: Record<string, boolean> = {};
  if (options?.voidCharge !== undefined) payload.void_charge = options.voidCharge;
  if (options?.voidStaffExpense !== undefined) payload.void_staff_expense = options.voidStaffExpense;
  const response = await apiClient.post<ActivityEnrollment>(
    `/api/v1/activity-enrollments/${id}/cancel/`,
    payload
  );
  return mapEnrollment(response.data);
}

/**
 * Promote a waitlisted enrollment to enrolled.
 * `force` grants the seat even at capacity (operator override → one over cap).
 */
export async function promoteEnrollment(
  id: string,
  options?: { force?: boolean }
): Promise<ActivityEnrollment> {
  const payload: Record<string, boolean> = {};
  if (options?.force !== undefined) payload.force = options.force;
  const response = await apiClient.post<ActivityEnrollment>(
    `/api/v1/activity-enrollments/${id}/promote/`,
    payload
  );
  return mapEnrollment(response.data);
}

/** Move an enrollment to another activity (same participant, same location). */
export async function moveEnrollment(
  id: string,
  targetActivityId: string
): Promise<ActivityEnrollment> {
  const response = await apiClient.post<ActivityEnrollment>(
    `/api/v1/activity-enrollments/${id}/move/`,
    { target_activity: targetActivityId }
  );
  return mapEnrollment(response.data);
}
